import { useId } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { TemplateFormTranslations } from "@/lib/i18n/types/documents-sections/templateForm";
import { panelDateLocale } from "@/domains/pm/utils/panelDates";
import {
  WEATHER_OPTIONS,
  type TemplateFormFieldDef,
  type WeatherOptionId,
} from "../../constants/templateForms";
import { toggleWeatherOption, type TemplateFormValue } from "../../utils/templateForm";
import type { SavedTemplateForm } from "../../hooks/useSavedTemplateForms";
import {
  SavedFormPicker,
  type SavedFormsProjectChoice,
  type SavedFormsState,
} from "./SavedFormPicker";

interface TemplateFormFieldProps {
  field: TemplateFormFieldDef;
  copy: TemplateFormTranslations;
  value: TemplateFormValue;
  onChange: (value: TemplateFormValue) => void;
  /** The forms this field can be imported from, when its schema names a
   *  source. Absent while the project has none to offer. */
  importSources?: SavedFormsState;
  /** Set only at building scope, so Import can ask which project to take the
   *  field from — the same question "Choose pre-filled form" asks. */
  savedFormsProject?: SavedFormsProjectChoice;
  /** Called with the form the user picked to import from. */
  onImport?: (form: SavedTemplateForm) => void;
}

/**
 * One labelled field of a template form. Every template renders through here,
 * so a template only ever declares a type — never its own markup.
 */
export function TemplateFormField({
  field,
  copy,
  value,
  onChange,
  importSources,
  savedFormsProject,
  onImport,
}: TemplateFormFieldProps) {
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const label = copy.fields[field.id];

  return (
    <div className={cn("space-y-1.5", !field.compact && "sm:col-span-2")}>
      <div className="flex items-center justify-between gap-2">
        {/* The weather group is a div — labelled via aria-labelledby, not htmlFor. */}
        <Label id={labelId} htmlFor={field.type === "weather" ? undefined : fieldId}>
          {label}
          {/* The marker sits outside the label text so it stays out of the
              field's accessible name — `required` on the control is what
              actually announces the requirement. It is a sibling rather than a
              child for the same reason. */}
        </Label>
        {field.required && (
          <span aria-hidden className="-ml-1 mr-auto text-destructive">
            *
          </span>
        )}
        {field.importFrom && importSources && (
          <SavedFormPicker
            copy={copy}
            forms={importSources}
            projectChoice={savedFormsProject}
            searchPlaceholder={copy.searchSavedForms}
            emptyLabel={copy.noSavedForms}
            onPick={(form) => onImport?.(form)}
            trigger={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 gap-1 px-2 text-xs text-muted-foreground"
              >
                <Download aria-hidden className="h-3 w-3" />
                {copy.importFrom}
              </Button>
            }
          />
        )}
      </div>
      <FieldControl
        fieldId={fieldId}
        labelId={labelId}
        field={field}
        copy={copy}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

interface FieldControlProps extends Omit<TemplateFormFieldProps, "onImport"> {
  fieldId: string;
  labelId: string;
}

function FieldControl({ fieldId, labelId, field, copy, value, onChange }: FieldControlProps) {
  const { language } = useLanguage();
  const text = typeof value === "string" ? value : "";

  if (field.type === "textarea") {
    return (
      <Textarea
        id={fieldId}
        rows={4}
        required={field.required}
        value={text}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.type === "date") {
    return (
      <DatePicker
        id={fieldId}
        value={text || null}
        onChange={(next) => onChange(next ?? "")}
        placeholder={copy.fields[field.id]}
        locale={panelDateLocale(language)}
      />
    );
  }
  if (field.type === "weather") {
    return (
      <WeatherGroup
        fieldId={fieldId}
        labelId={labelId}
        copy={copy}
        value={value}
        onChange={onChange}
      />
    );
  }

  if (field.type === "time") {
    return (
      <TimePicker
        id={fieldId}
        value={text}
        onChange={onChange}
        placeholder={copy.time.placeholder}
        hoursLabel={copy.time.hours}
        minutesLabel={copy.time.minutes}
        clearLabel={copy.time.clear}
      />
    );
  }

  const inputType = field.type === "number" ? field.type : "text";
  return (
    <Input
      id={fieldId}
      type={inputType}
      required={field.required}
      value={text}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function WeatherGroup({
  fieldId,
  labelId,
  copy,
  value,
  onChange,
}: Omit<FieldControlProps, "field">) {
  const selected: WeatherOptionId[] = Array.isArray(value) ? (value as WeatherOptionId[]) : [];

  return (
    <div role="group" aria-labelledby={labelId} className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
      {WEATHER_OPTIONS.map((option) => {
        const optionId = `${fieldId}-${option}`;
        return (
          <div key={option} className="flex items-center gap-2">
            <Checkbox
              id={optionId}
              checked={selected.includes(option)}
              onCheckedChange={() => onChange(toggleWeatherOption(selected, option))}
            />
            <Label htmlFor={optionId} className="font-normal">
              {copy.weather[option]}
            </Label>
          </div>
        );
      })}
    </div>
  );
}
